import { IPartners, IRentsyncProperties, IRentsyncTrackingItems } from '@llws/lift-entity-interfaces';
import { IQueryCommandOptions, Equal, Null, Not, In } from '@llws/dynamic-router-utils';
import {
  IRentsyncPropertyModifyPayload,
  IPropertySyndicationData,
  ISyndicationGridProperty,
  IMetaResponse,
} from '@/shared';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { DataModifyProcessor } from '@/shared/classes/data-modify-processor';
import { IAction, IStateStrict } from '@/shared/interfaces/store-base';
import config from '@/config';
import { StoreBaseImplementationV2 } from '@/shared/classes/store-base-implementation-v2';
import { IFilterPayloadV2 } from '@/shared/interfaces/filter-payload-v2';

// Request URLs
const rentsyncPropertyUrl = `${config.adminGateway}/syndication-api/rentsync_properties`;
const rentsyncTrackingItemsUrl = `${config.adminGateway}/syndication-api/rentsync_tracking_items`;

// Base store configuration
export const alias = 'p';
export const buildingTypeAlias = 'bt';
export const cityAlias = 'ci';

const adminGatewayResourcePath: string = '/property-api/properties';
const listViewName: string = 'syndication-grid';
const showLogging: boolean = false;

const defaultGetListFilters: IQueryCommandOptions = {
  alias,
  relations: [
    {
      path: `${alias}.buildingType`,
      alias: buildingTypeAlias,
    },
    {
      path: `${alias}.rentsyncProperties`,
      alias: 'rp',
    },
    {
      path: `${alias}.city`,
      alias: 'ci',
    },
    {
      path: `ci.province`,
      alias: 'pr',
    },
  ],
  select: [
    `${alias}.id`,
    `${alias}.clientId`,
    `${alias}.buildingName`,
    `${alias}.streetName`,
    `${alias}.streetNumber`,
    `${alias}.status`,
    `${alias}.phone`,
    `${alias}.billingTypeId`,
    `${alias}.sfRestrictUnits`,
    `${cityAlias}.id`,
    `${cityAlias}.cityName`,
    `pr.id`,
    `pr.provinceName`,
    `pr.provinceCode`,
    `pr.countryId`,
    `${buildingTypeAlias}.id`,
    `${buildingTypeAlias}.name`,
    `${buildingTypeAlias}.categoryType`,
    `${buildingTypeAlias}.parentKey`,
  ],
  where: [
    Not(In(`${alias}.status`, ['deleted'])),
  ],
  limit: 10000,
};

const defaultGetSingleFilters: IQueryCommandOptions = {
  alias: 'building',
  relations: [
    {
      path: 'rentsyncProperties',
      alias: 'rs',
    },
    {
      path: 'buildingsHasPhotos',
      alias: 'bhp',
      whereCommands: [Equal('orderBy', 'minPhotoOrderBy')],
    },
    {
      path: 'bhp.listingPhoto',
      alias: 'bhplp',
    },
    {
      path: 'rentsyncPropertiesMetas',
      alias: 'rpm',
      whereCommands: [Null('suiteTypeId'), Equal('metaKey', 'rentsync_ad_url')],
    },
    {
      path: 'rentsyncTrackingItems',
      alias: 'rti',
      whereCommands: [In('field', ['building_phone', 'building_email', 'custom_url'])],
    },
  ],

  aggregations: [
    {
      aggregate: 'MIN',
      table: 'buildings_has_photos',
      column: 'orderBy',
      propertyAlias: 'minPhotoOrderBy',
      whereCommands: [Equal('buildingId', 'building.id')],
    },
  ],
};

interface IAdUrlsBreakdown {
  buildingIds: number[];
  adUrlsCount: number;
}
interface IClientAdUrlsBreakdown {
  [key: string]: IAdUrlsBreakdown;
}
interface ISyndicationGridPropertyMeta extends IMetaResponse {
  partners: IPartners[];
  adUrlsBreakdown: IClientAdUrlsBreakdown;
}

// Instantiate the base store with the params
const base = new StoreBaseImplementationV2<ISyndicationGridProperty>({
  adminGatewayResourcePath,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
});

// Extend StoreBaseImplementation object to add custom state/getters/mutations/actions.
export default {
  namespaced: base.namespaced, // 'properties-syndication/'

  state: {
    ...base.state,
  },

  getters: {
    ...base.getters,

    partners: (state: IStateStrict<ISyndicationGridProperty>) => {
      if (state.list) {
        return (state.list.meta as ISyndicationGridPropertyMeta)?.partners;
      }

      return [];
    },

    adUrlsBreakdown: (state: IStateStrict<ISyndicationGridProperty>) => {
      if (state.list) {
        return (state.list.meta as ISyndicationGridPropertyMeta)?.adUrlsBreakdown;
      }
      return {};
    },
  },

  mutations: {
    ...base.mutations,
  },

  actions: {
    ...base.actions,
    ...{
      saveRentsyncProperties: async (
        ctx: IAction<IStateStrict<ISyndicationGridProperty>>,
        payload: IRentsyncPropertyModifyPayload,
      ) => {
        // Build data processor
        const processor = new DataModifyProcessor<IRentsyncProperties>(rentsyncPropertyUrl);

        payload.buildings.forEach((b) => {
          let rentsyncProperty: IRentsyncProperties | undefined = b.rentsyncProperties!.find(
            (rp) => rp.partnerKey === payload.partner.partnerKey,
          );

          if (rentsyncProperty) {
            rentsyncProperty.state = payload.newState;
            processor.putItems.push(rentsyncProperty);
          } else if (payload.newState !== 0) {
            rentsyncProperty = {
              id: undefined,
              clientId: b.clientId,
              buildingId: b.id!,
              partnerKey: payload.partner.partnerKey,
              state: payload.newState,
              created: new Date(),
            };
            processor.postItems.push(rentsyncProperty);
          }
        });

        const result = await processor.execute();

        // Set state with processor results
        const current: ISyndicationGridProperty[] = ctx.getters.list;

        (result.putResult?.data || []).forEach((p: IRentsyncProperties) => {
          const propertyIdx = current.findIndex((b) => b.id === p.buildingId);
          const rsPropertyIdx = current[propertyIdx].rentsyncProperties!.findIndex((rp) => rp.id === p.id);
          current[propertyIdx].rentsyncProperties![rsPropertyIdx] = { ...p };
        });

        (result.postResult?.data || []).forEach((p: IRentsyncProperties) => {
          const propertyIdx = current.findIndex((b) => b.id === p.buildingId);
          current[propertyIdx].rentsyncProperties!.push(p);
        });

        ctx.commit('set', current);
      },

      getPropertySidebarSingle: async (ctx: IAction<IStateStrict<ISyndicationGridProperty>>, payload: IFilterPayloadV2) => {
        const resourcePath = '/property-api/properties/syndication';
        const result = await adminRequest.get(`${resourcePath}/${payload.id}?where=clientId:${payload.clientId}`);
        ctx.commit('set', result);
        return result;
      },

      saveSyndicationDataByProperty: async (
        ctx: IAction<IStateStrict<ISyndicationGridProperty>>,
        payload: { building: ISyndicationGridProperty, data: IPropertySyndicationData[] },
      ) => {
        // Build data processors
        const rentsyncPropertyProcessor = new DataModifyProcessor<IRentsyncProperties>(rentsyncPropertyUrl);
        const rentsyncTrackingProcessor = new DataModifyProcessor<IRentsyncTrackingItems>(rentsyncTrackingItemsUrl);

        payload.data.forEach((d) => {
          // Process new / existing rentsync_properties records
          if (
            (d.rentsyncProperty.state === 1 && !d.rentsyncProperty.id)
            || (d.rentsyncProperty.customUrl && !d.rentsyncProperty.id)
          ) {
            rentsyncPropertyProcessor.postItems.push(d.rentsyncProperty);
          } else if (d.rentsyncProperty.id) {
            rentsyncPropertyProcessor.putItems.push(d.rentsyncProperty);
          }

          // Process new / existing rentsync_tracking_items records
          if (d.rentsyncTrackingEmail.value && !d.rentsyncTrackingEmail.id) {
            rentsyncTrackingProcessor.postItems.push(d.rentsyncTrackingEmail);
          } else if (d.rentsyncTrackingEmail.id) {
            rentsyncTrackingProcessor.putItems.push(d.rentsyncTrackingEmail);
          }

          if (d.rentsyncTrackingPhone.value && !d.rentsyncTrackingPhone.id) {
            rentsyncTrackingProcessor.postItems.push(d.rentsyncTrackingPhone);
          } else if (d.rentsyncTrackingPhone.id) {
            rentsyncTrackingProcessor.putItems.push(d.rentsyncTrackingPhone);
          }

          if (d.rentsyncTrackingCustomUrl.value && !d.rentsyncTrackingCustomUrl.id) {
            rentsyncTrackingProcessor.postItems.push(d.rentsyncTrackingCustomUrl);
          } else if (d.rentsyncTrackingCustomUrl.id) {
            rentsyncTrackingProcessor.putItems.push(d.rentsyncTrackingCustomUrl);
          }
        });

        // Process
        const rentsyncPropertiesResult = await rentsyncPropertyProcessor.execute();
        const rentsyncTrackingResult = await rentsyncTrackingProcessor.execute();

        // Set state with processors' results
        const currentList: ISyndicationGridProperty[] = ctx.getters.list;
        const currentSingle: ISyndicationGridProperty = ctx.getters.single;

        const listIdx = currentList.findIndex((b) => b.id === payload.building.id);

        if (listIdx !== -1 && rentsyncPropertiesResult.modifiedResultSet.length > 0) {
          currentList[listIdx].rentsyncProperties = rentsyncPropertiesResult.modifiedResultSet;
          ctx.commit('set', currentList);
        }

        if (currentSingle) {
          currentSingle.rentsyncProperties = rentsyncPropertiesResult.modifiedResultSet.length > 0
            ? rentsyncPropertiesResult.modifiedResultSet : currentSingle.rentsyncProperties;
          currentSingle.rentsyncTrackingItems = rentsyncTrackingResult.modifiedResultSet.length > 0
            ? rentsyncTrackingResult.modifiedResultSet : currentSingle.rentsyncTrackingItems;
          ctx.commit('set', currentSingle);
        }
      },

      saveSyndicationDataByPropertyV2: async (
        ctx: IAction<IStateStrict<ISyndicationGridProperty>>,
        payload: { building: ISyndicationGridProperty, data: IPropertySyndicationData[] },
      ) => {
        const rentsyncPropertyProcessor = new DataModifyProcessor<IRentsyncProperties>(rentsyncPropertyUrl);
        const rentsyncTrackingProcessor = new DataModifyProcessor<IRentsyncTrackingItems>(rentsyncTrackingItemsUrl);

        payload.data.forEach((d) => {
          if (
            (d.rentsyncProperty.state === 1 && !d.rentsyncProperty.id)
            || (d.rentsyncProperty.customUrl && !d.rentsyncProperty.id)
          ) {
            rentsyncPropertyProcessor.postItems.push(d.rentsyncProperty);
          } else if (d.rentsyncProperty.id) {
            rentsyncPropertyProcessor.putItems.push(d.rentsyncProperty);
          }

          if (d.rentsyncTrackingEmail.value && !d.rentsyncTrackingEmail.id) {
            rentsyncTrackingProcessor.postItems.push(d.rentsyncTrackingEmail);
          } else if (d.rentsyncTrackingEmail.id) {
            rentsyncTrackingProcessor.putItems.push(d.rentsyncTrackingEmail);
          }

          if (d.rentsyncTrackingPhone.value && !d.rentsyncTrackingPhone.id) {
            rentsyncTrackingProcessor.postItems.push(d.rentsyncTrackingPhone);
          } else if (d.rentsyncTrackingPhone.id) {
            rentsyncTrackingProcessor.putItems.push(d.rentsyncTrackingPhone);
          }

          if (d.rentsyncTrackingCustomUrl.value && !d.rentsyncTrackingCustomUrl.id) {
            rentsyncTrackingProcessor.postItems.push(d.rentsyncTrackingCustomUrl);
          } else if (d.rentsyncTrackingCustomUrl.id) {
            rentsyncTrackingProcessor.putItems.push(d.rentsyncTrackingCustomUrl);
          }
        });

        const rentsyncPropertiesResult = await rentsyncPropertyProcessor.execute();
        const rentsyncTrackingResult = await rentsyncTrackingProcessor.execute();

        const currentSingle: ISyndicationGridProperty = ctx.getters.single;
        if (currentSingle) {
          currentSingle.rentsyncProperties = rentsyncPropertiesResult.modifiedResultSet.length > 0
            ? rentsyncPropertiesResult.modifiedResultSet : currentSingle.rentsyncProperties;
          currentSingle.rentsyncTrackingItems = rentsyncTrackingResult.modifiedResultSet.length > 0
            ? rentsyncTrackingResult.modifiedResultSet : currentSingle.rentsyncTrackingItems;
          ctx.commit('set', currentSingle);
        }
      },

      getSingleFamilyData: async (ctx: IAction<IStateStrict<ISyndicationGridProperty>>, payload: { partner: IPartners, buildingId: number }) => {
        const { partner, buildingId } = payload;
        const resourcePath = '/syndication-api/single_family_check/';
        const result = await adminRequest.get(`${resourcePath}${partner.partnerKey}/${buildingId}`);
        ctx.commit('set', result);
        return result;
      },
    },
  },
};
